// src/pages/ProjectsPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Chip,
  Box,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
  Paper,
  Pagination,
  Drawer
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Science as ScienceIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Folder as FolderIcon,
  Person as PersonIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { projectsAPI, authAPI } from '../services/api';
import { getCached, requestCacheKey } from '../services/requestCache';

// Импортируем новый компонент StackedCard
import StackedCard from '../components/UI/StackedCard';

// Импортируем данные из справочников
import {
  RESEARCH_FIELDS,
  POPULAR_RESEARCH_AREAS
} from '../data/scienceData';

// 🔥 ИМПОРТИРУЕМ ЦВЕТА И ИКОНКИ ИЗ НОВОГО ФАЙЛА
import {
  getFieldColor,
  getCompetencyIcon,
  getProjectTypeIcon,
  getStatusColor
} from '../data/themeData';

// ============================================
// КОНСТАНТЫ
// ============================================

const PROJECT_STATUSES = [
  { value: '', label: 'Все статусы' },
  { value: 'active', label: 'Активные' },
  { value: 'recruiting', label: 'Ищут участников' },
  { value: 'draft', label: 'Черновики' },
  { value: 'completed', label: 'Завершенные' }
];

const PROJECT_TYPES = [
  { value: '', label: 'Все типы' },
  { value: 'research_paper', label: 'Научная статья' },
  { value: 'dissertation', label: 'Диссертация' },
  { value: 'grant', label: 'Грантовый проект' },
  { value: 'conference', label: 'Конференция' },
  { value: 'book', label: 'Книга/Монография' },
  { value: 'creative', label: 'Творческий проект' },
  { value: 'other', label: 'Другой проект' }
];

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================

const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  paddingTop: 0,
  paddingBottom: theme.spacing(6),
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));

// 🔥 АНИМИРОВАННАЯ ШАПКА
const AnimatedHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '960px',
  minWidth: '800px',
  minHeight: 300,
  marginTop: '-100px',
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(135deg, #284b63 0%, #3c6e71 50%, #58b6b9 100%)',
  animation: 'AnimateBG 10s ease infinite',
  backgroundSize: '300% 300%',
  overflow: 'hidden',
  boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.2)',
  '@keyframes AnimateBG': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  },
  [theme.breakpoints.down('md')]: {
    width: 'calc(100% - 32px)',
    minWidth: 'auto',
    marginLeft: '16px',
    marginRight: '16px'
  }
}));

const CubesContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 1
});

const Cube = styled(Box)(({ delay, size, left }) => ({
  position: 'absolute',
  display: 'block',
  listStyle: 'none',
  width: size || '20px',
  height: size || '20px',
  background: 'rgba(255, 255, 255, 0.15)',
  bottom: '-150px',
  left: left || 'auto',
  animation: 'animate 25s ease-in infinite',
  animationDelay: delay || '0s',
  '@keyframes animate': {
    '0%': {
      transform: 'translateY(0) rotate(0deg)',
      opacity: 1,
      borderRadius: 0
    },
    '100%': {
      transform: 'translateY(-1000px) rotate(720deg)',
      opacity: 0,
      borderRadius: '50%'
    }
  }
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: 110,
  zIndex: 2,
  padding: theme.spacing(2, 5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  color: theme.palette.common.white
}));

const CreateProjectButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(3),
  left: theme.spacing(5),
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  border: '0.5px solid rgba(255, 255, 255, 0.8)',
  zIndex: 10,
  padding: theme.spacing(1),
  backdropFilter: 'blur(4px)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderColor: 'white'
  }
}));

const StatChip = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(3),
  backgroundColor: 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(4px)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 960,
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none'
}));

// 🔥 Компонент для компетенций
const CompetencyChip = styled(Chip)(({ theme }) => ({
  margin: 0,
  height: 24,
  maxWidth: 82,
  flexShrink: 1,
  borderRadius: '999px',
  fontSize: '0.68rem',
  backgroundColor: alpha(theme.palette.primary.main, 0.07),
  color: alpha('#102A2E', 0.72),
  border: `1px solid ${alpha('#ffffff', 0.72)}`,
  boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.8)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.11),
  },
  '& .MuiChip-label': {
    px: 0.85,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiChip-icon': {
    marginLeft: theme.spacing(0.45),
    marginRight: theme.spacing(-0.1),
    fontSize: '0.9rem',
    flexShrink: 0,
  }
}));

const ProjectsGrid = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 960,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: theme.spacing(5, 3),
  marginBottom: theme.spacing(4)
}));


// ============================================
// SKELETON КАРТОЧКИ ПРОЕКТА
// ============================================

const ProjectCardSkeleton = ({ theme }) => (
  <Box
    sx={{
      position: 'relative',
      minHeight: 360,
      width: '100%',
      filter: 'drop-shadow(0 8px 24px rgba(25, 40, 45, 0.06))'
    }}
  >
    <Paper
      elevation={0}
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: 3,
        transform: 'translate(16px, 22px)',
        background: 'rgba(255,255,255,0.56)',
        border: '1px solid rgba(255,255,255,0.7)'
      }}
    />
    <Paper
      elevation={0}
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: 3,
        transform: 'translate(8px, 14px)',
        background: 'rgba(255,255,255,0.72)',
        border: '1px solid rgba(255,255,255,0.76)'
      }}
    />
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        zIndex: 2,
        minHeight: 360,
        height: '100%',
        overflow: 'hidden',
        borderRadius: 3,
        p: 2.2,
        background: 'linear-gradient(145deg, rgba(255,255,255,0.92), rgba(245,249,249,0.82))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.86)',
        boxShadow: '0 8px 24px rgba(25, 40, 45, 0.08)'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          <Skeleton animation="wave" variant="rounded" width={30} height={30} sx={{ borderRadius: 2 }} />
          <Skeleton animation="wave" variant="text" width="62%" height={28} />
        </Box>
        <Skeleton animation="wave" variant="rounded" width={72} height={26} sx={{ borderRadius: 3 }} />
      </Box>

      <Skeleton animation="wave" variant="text" width="94%" height={22} />
      <Skeleton animation="wave" variant="text" width="78%" height={22} sx={{ mb: 1.5 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 2 }}>
        <Skeleton animation="wave" variant="rounded" width={76} height={25} sx={{ borderRadius: 3 }} />
        <Skeleton animation="wave" variant="rounded" width={92} height={25} sx={{ borderRadius: 3 }} />
        <Skeleton animation="wave" variant="rounded" width={66} height={25} sx={{ borderRadius: 3 }} />
      </Box>

      <Box sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 2.5,
        background: alpha(theme.palette.primary.main, 0.035),
        border: '1px solid rgba(255,255,255,0.72)'
      }}>
        <Skeleton animation="wave" variant="text" width="42%" height={19} />
        <Skeleton animation="wave" variant="text" width="88%" height={19} />
        <Skeleton animation="wave" variant="text" width="64%" height={19} />
      </Box>

      <Box sx={{
        position: 'absolute',
        left: 18,
        right: 18,
        bottom: 18,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1
      }}>
        <Skeleton animation="wave" variant="rounded" width={108} height={28} sx={{ borderRadius: 3 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton animation="wave" variant="text" width={70} height={20} />
          <Skeleton animation="wave" variant="text" width={82} height={20} />
        </Box>
      </Box>
    </Paper>
  </Box>
);

const ProjectCardsSkeleton = ({ theme, count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <ProjectCardSkeleton key={`project-skeleton-${index}`} theme={theme} />
    ))}
  </>
);

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4)
}));

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

function ProjectsPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const savedState = (() => { try { return JSON.parse(sessionStorage.getItem('projectsPageState') || 'null') || {}; } catch (_) { return {}; } })();
  const initialFilters = savedState.filters || { search: '', status: '', type: '', branch: '' };
  const initialPage = Number(savedState.page) > 0 ? Number(savedState.page) : 1;
  const initialParams = { page: initialPage, page_size: 12, search: initialFilters.search || undefined, status: initialFilters.status || undefined, type: initialFilters.type || undefined, branch: initialFilters.branch || undefined };
  const initialCached = getCached(requestCacheKey('projects-list', initialParams));
  const [projects, setProjects] = useState(initialCached?.results || (Array.isArray(initialCached) ? initialCached : []));
  const [loading, setLoading] = useState(!initialCached);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(initialCached?.count ? Math.max(1, Math.ceil(initialCached.count / 12)) : 1);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    authAPI.getBranches().then(setBranches).catch(err => console.error('Не удалось загрузить филиалы:', err));
  }, []);

  // Данные для кубиков
  const cubes = [
    { left: '5%', size: '60px', delay: '0s' },
    { left: '15%', size: '25px', delay: '2s' },
    { left: '25%', size: '40px', delay: '4s' },
    { left: '35%', size: '80px', delay: '1s' },
    { left: '45%', size: '20px', delay: '3s' },
    { left: '55%', size: '70px', delay: '5s' },
    { left: '65%', size: '35px', delay: '2s' },
    { left: '75%', size: '50px', delay: '6s' },
    { left: '85%', size: '30px', delay: '4s' },
    { left: '95%', size: '45px', delay: '0s' },
  ];

  useEffect(() => {
    fetchProjects();
  }, [page, filters]);

  const fetchProjects = async () => {
    const params = { page, page_size: 12, search: filters.search || undefined, status: filters.status || undefined, type: filters.type || undefined, branch: filters.branch || undefined };
    const cached = getCached(requestCacheKey('projects-list', params));
    if (!cached && projects.length === 0) setLoading(true);
    try {
      const response = await projectsAPI.getAll(params);

      console.log('📦 Ответ API:', response);

      // API возвращает {count, results, next, previous}
      if (response && response.results) {
        setProjects(response.results);
        setTotalPages(Math.ceil((response.count || 0) / 12));
        setTotalProjects(response.count || 0);
      } else if (Array.isArray(response)) {
        setProjects(response);
        setTotalPages(Math.ceil(response.length / 12));
      } else {
        setProjects([]);
        setTotalPages(1);
      }

      setError(null);
    } catch (error) {
      console.error('Ошибка загрузки проектов:', error);
      setError(error.message || 'Не удалось загрузить проекты. Проверьте подключение к серверу.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => {
    try { sessionStorage.setItem('projectsPageState', JSON.stringify({ page, filters, scrollY: window.scrollY })); } catch (_) {}
  }, [page, filters]);

  useEffect(() => {
    try { sessionStorage.setItem('projectsPageState', JSON.stringify({ page, filters, scrollY: window.scrollY })); } catch (_) {}
  }, [page, filters]);
  useEffect(() => {
    if (!loading) {
      try { const saved = JSON.parse(sessionStorage.getItem('projectsPageState') || 'null'); if (saved && Number.isFinite(saved.scrollY)) requestAnimationFrame(() => window.scrollTo(0, saved.scrollY)); } catch (_) {}
    }
  }, [loading]);

  // Статистика
  const [totalProjects, setTotalProjects] = useState(0);
  const activeProjectsCount = Array.isArray(projects)
    ? projects.filter(p => p && p.status === 'active').length
    : 0;
  const recruitingProjectsCount = Array.isArray(projects)
    ? projects.filter(p => p && p.status === 'recruiting').length
    : 0;

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ search: '', status: '', type: '', branch: '' });
    setPage(1);
  };

  const hasActiveFilters = filters.search || filters.status || filters.type || filters.branch;

  const getStatusText = (status) => {
    const statusMap = {
      'draft': 'Черновик',
      'recruiting': 'Ищут участников',
      'active': 'Активный',
      'completed': 'Завершен',
      'on_hold': 'На паузе',
      'archived': 'В архиве'
    };
    return statusMap[status] || status;
  };

  const getProjectTypeText = (type) => {
    const typeMap = {
      'research_paper': 'Научная статья',
      'dissertation': 'Диссертация',
      'grant': 'Грантовый проект',
      'conference': 'Конференция',
      'book': 'Книга/Монография',
      'creative': 'Творческий проект',
      'other': 'Другой проект'
    };
    return typeMap[type] || type;
  };

  const getResearchFieldCategory = (field) => {
    if (!field) return null;
    for (const [key, category] of Object.entries(RESEARCH_FIELDS)) {
      if (category.items.includes(field)) {
        return category.name;
      }
    }
    return null;
  };

  const isPopularField = (field) => {
    if (!field) return false;
    return POPULAR_RESEARCH_AREAS.includes(field);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указан';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return 'Некорректная дата';
    }
  };

  const safeGet = (project, property, defaultValue = '') => {
    if (!project || typeof project !== 'object') return defaultValue;
    return project[property] !== undefined ? project[property] : defaultValue;
  };

  return (
    <PageContainer>
      <AnimatedHeader>
        <CubesContainer>
          {cubes.map((cube, index) => (
            <Cube
              key={index}
              left={cube.left}
              size={cube.size}
              delay={cube.delay}
            />
          ))}
        </CubesContainer>

        <Tooltip title="Создать проект">
          <CreateProjectButton onClick={() => navigate('/projects/create')} size="small">
            <AddIcon sx={{ fontSize: 24 }} />
          </CreateProjectButton>
        </Tooltip>

        <HeaderContent>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            height: '100%',
            width: '100%'
          }}>
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h3"
                fontWeight="900"
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  letterSpacing: '-0.5px',
                  opacity: '0.9',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  textAlign: 'left'
                }}
              >
                НАУЧНЫЕ ПРОЕКТЫ
              </Typography>
            </Box>

            <Box sx={{ width: '100%', mt: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 100,
                  fontSize: 18,
                  opacity: 0.7,
                  textAlign: 'left'
                }}
              >
                Платформа для науки, творчества и объединения усилий
              </Typography>
            </Box>

            <Box sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2
            }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <StatChip elevation={0}>
                  <FolderIcon fontSize="small" />
                  <Typography variant="body2">Всего: {totalProjects}</Typography>
                </StatChip>
                <StatChip elevation={0}>
                  <Typography variant="body2">Активных: {activeProjectsCount}</Typography>
                </StatChip>
                <StatChip elevation={0}>
                  <Typography variant="body2">Ищут: {recruitingProjectsCount}</Typography>
                </StatChip>
              </Box>
            </Box>
          </Box>
        </HeaderContent>
      </AnimatedHeader>

      <FilterSection elevation={0} sx={{ background: (theme) => alpha(theme.palette.background.paper, 0.72), backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)', border: '1px solid', borderColor: 'rgba(255,255,255,0.42)', boxShadow: '0 14px 40px rgba(15,23,42,0.055)' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            sx={{ flex: '1 1 280px', '& .MuiOutlinedInput-root': { borderRadius: '18px', backgroundColor: alpha(theme.palette.primary.main, 0.025) } }}
            placeholder="Поиск проектов..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'primary.main' }} /></InputAdornment>,
              endAdornment: filters.search ? <InputAdornment position="end"><IconButton size="small" onClick={() => handleFilterChange('search', '')}><ClearIcon /></IconButton></InputAdornment> : null
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFiltersOpen(true)}
            sx={{ minHeight: 42, px: 2.2, borderRadius: '16px', textTransform: 'none', fontWeight: 600, borderColor: alpha(theme.palette.primary.main, 0.28), background: alpha(theme.palette.background.paper, 0.55), backdropFilter: 'blur(12px)', '&:hover': { background: alpha(theme.palette.primary.main, 0.07), borderColor: 'primary.main' } }}
          >
            Фильтры{hasActiveFilters ? ` (${[filters.status, filters.type, filters.branch].filter(Boolean).length})` : ''}
          </Button>
          {hasActiveFilters && <Button onClick={handleClearFilters} color="inherit" sx={{ borderRadius: '14px', textTransform: 'none' }}>Сбросить</Button>}
        </Box>
      </FilterSection>

      <Drawer anchor="right" open={filtersOpen} onClose={() => setFiltersOpen(false)} PaperProps={{ sx: {
    width: { xs: 'min(100vw, 420px)', sm: 420 },
    maxWidth: '100vw',
    boxSizing: 'border-box',
    p: 0,
    overflow: 'hidden',
    borderRadius: { xs: '24px 0 0 24px', sm: '28px 0 0 28px' },
    border: '1px solid rgba(255,255,255,0.62)',
    background: (theme) => theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(28,32,38,0.94), rgba(22,25,31,0.88))'
      : 'linear-gradient(145deg, rgba(255,255,255,0.94), rgba(245,248,250,0.88))',
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    boxShadow: '-24px 0 70px rgba(15,23,42,0.20)',
    display: 'flex',
    flexDirection: 'column'
  } }}>
        <Box sx={{
      px: 3, py: 2.5,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: '1px solid', borderColor: 'divider',
      background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.48)'
    }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Фильтры проектов</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>Настройте список под себя</Typography>
          </Box>
          <IconButton onClick={() => setFiltersOpen(false)} aria-label="Закрыть фильтры" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px' }}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Статус проекта</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.2, '& .MuiOutlinedInput-root': { borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }, '& .MuiInputLabel-root': { fontWeight: 500 } }}>
            <InputLabel>Статус</InputLabel>
            <Select value={filters.status} label="Статус" onChange={(e) => handleFilterChange('status', e.target.value)}>
              {PROJECT_STATUSES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Тип проекта</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.2, '& .MuiOutlinedInput-root': { borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }, '& .MuiInputLabel-root': { fontWeight: 500 } }}>
            <InputLabel>Тип проекта</InputLabel>
            <Select value={filters.type} label="Тип проекта" onChange={(e) => handleFilterChange('type', e.target.value)}>
              {PROJECT_TYPES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Филиал владельца</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.2, '& .MuiOutlinedInput-root': { borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }, '& .MuiInputLabel-root': { fontWeight: 500 } }}>
            <InputLabel>Филиал</InputLabel>
            <Select value={filters.branch || ''} label="Филиал" onChange={(e) => handleFilterChange('branch', e.target.value)}>
              <MenuItem value="">Все филиалы</MenuItem>
              {branches.map((branch) => <MenuItem key={branch.id} value={String(branch.id)}>{branch.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ p: 2.5, mt: 'auto', display: 'flex', gap: 1.5, borderTop: '1px solid', borderColor: 'divider', background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.52)' }}>
          <Button fullWidth variant="outlined" onClick={handleClearFilters} sx={{ borderRadius: '15px', py: 1.2, textTransform: 'none' }}>Сбросить</Button>
          <Button fullWidth variant="contained" onClick={() => setFiltersOpen(false)} sx={{ borderRadius: '15px', py: 1.2, textTransform: 'none', boxShadow: 'none' }}>Готово</Button>
        </Box>
      </Drawer>

      {error && (
        <Alert severity="error" sx={{ maxWidth: 1200, width: '100%', mb: 3, borderRadius: 3 }}>
          {error}
          <Button color="inherit" size="small" onClick={fetchProjects} sx={{ ml: 2 }}>
            Повторить
          </Button>
        </Alert>
      )}

      {loading && projects.length === 0 ? (
        <ProjectsGrid>
          <ProjectCardsSkeleton theme={theme} count={6} />
        </ProjectsGrid>
      ) : Array.isArray(projects) && projects.length > 0 ? (
        <ProjectsGrid>
          {projects.map((project) => {
            if (!project || typeof project !== 'object') return null;

            const projectId = safeGet(project, 'id');
            const projectTitle = safeGet(project, 'title', 'Без названия');
            const projectStatus = safeGet(project, 'status');
            const projectType = safeGet(project, 'project_type');
            const scientificField = safeGet(project, 'scientific_field');
            const description = safeGet(project, 'description', '');
            const memberCount = typeof safeGet(project, 'member_count') === 'number'
              ? safeGet(project, 'member_count')
              : 0;
            const deadline = safeGet(project, 'deadline');

            const fieldCategory = getResearchFieldCategory(scientificField);
            const fieldColor = getFieldColor(scientificField); // 🔥 ПОЛУЧАЕМ ЦВЕТ ИЗ НОВОГО ФАЙЛА

            const competencies = safeGet(project, 'required_competencies', []);
            const displayCompetencies = competencies.slice(0, 3);
            const remainingCompetencies = competencies.length - 3;

            return (
              <StackedCard
                key={projectId}
                onClick={() => navigate(`/projects/${projectId}`)}
                gradientColor={fieldColor} // 🔥 ИСПОЛЬЗУЕМ ПОЛУЧЕННЫЙ ЦВЕТ
              >
                {{
                  top: (
                    <>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        minHeight: 28,
                        mb: 0.7
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.7,
                          minWidth: 0
                        }}>
                          <Box sx={{
                            width: 30,
                            height: 30,
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: '10px',
                            backgroundColor: alpha(fieldColor, 0.12),
                            border: `1px solid ${alpha(fieldColor, 0.20)}`,
                            color: fieldColor,
                            '& svg': { fontSize: 18 }
                          }}>
                            {getProjectTypeIcon(projectType)}
                          </Box>
                          {isPopularField(scientificField) && (
                            <Tooltip title="Популярное научное направление">
                              <Box sx={{
                                width: 24,
                                height: 24,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: '50%',
                                backgroundColor: alpha('#ffb74d', 0.12),
                                fontSize: 13
                              }}>
                                ⭐
                              </Box>
                            </Tooltip>
                          )}
                        </Box>

                        <Chip
                          label={getStatusText(projectStatus)}
                          size="small"
                          variant="outlined"
                          sx={{
                            flexShrink: 0,
                            height: 22,
                            maxWidth: 108,
                            borderRadius: '999px',
                            fontWeight: 600,
                            fontSize: '0.64rem',
                            backgroundColor: alpha(getStatusColor(projectStatus), 0.08),
                            color: getStatusColor(projectStatus),
                            borderColor: alpha(getStatusColor(projectStatus), 0.22),
                            '& .MuiChip-label': {
                              px: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.25,
                          fontSize: '1rem',
                          height: '3.75rem',
                          mb: 0.65,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          letterSpacing: '-0.015em',
                          color: alpha('#102A2E', 0.96),
                        }}
                      >
                        {projectTitle}
                      </Typography>

                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.55,
                        height: 22,
                        minWidth: 0,
                        mb: 0.45,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }}>
                        <Typography
                          variant="caption"
                          sx={{
                            flexShrink: 0,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: alpha('#102A2E', 0.62)
                          }}
                        >
                          {getProjectTypeText(projectType)}
                        </Typography>

                        {scientificField && (
                          <>
                            <Box sx={{
                              width: 4,
                              height: 4,
                              flexShrink: 0,
                              borderRadius: '50%',
                              backgroundColor: fieldColor
                            }} />
                            <Typography
                              variant="caption"
                              sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.65rem',
                                color: alpha('#102A2E', 0.58)
                              }}
                            >
                              {scientificField}
                            </Typography>
                          </>
                        )}

                        {fieldCategory && (
                          <Chip
                            label={fieldCategory}
                            size="small"
                            variant="outlined"
                            sx={{
                              flexShrink: 0,
                              height: 18,
                              maxWidth: 86,
                              borderRadius: '999px',
                              fontSize: '0.55rem',
                              borderColor: alpha(fieldColor, 0.22),
                              color: alpha('#102A2E', 0.62),
                              backgroundColor: alpha(fieldColor, 0.06),
                              '& .MuiChip-label': {
                                px: 0.8,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          height: '2.25rem',
                          mb: 0.65,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          fontSize: '0.73rem',
                          fontWeight: 400,
                          color: alpha('#102A2E', 0.64),
                          lineHeight: 1.45,
                          textAlign: 'left'
                        }}
                      >
                        {description || 'Нет описания'}
                      </Typography>

                      {displayCompetencies.length > 0 && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          gap: 0.45,
                          height: 26,
                          minHeight: 26,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          mt: 'auto'
                        }}>
                          {displayCompetencies.map((comp, idx) => (
                            <CompetencyChip
                              key={idx}
                              icon={<span>{getCompetencyIcon(comp)}</span>}
                              label={comp}
                              size="small"
                            />
                          ))}
                          {remainingCompetencies > 0 && (
                            <CompetencyChip
                              label={`+${remainingCompetencies}`}
                              size="small"
                              sx={{ flexShrink: 0 }}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  ),

                  bottom: (
                    <Box sx={{ width: '100%' }}>
                      {project.owner && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 0.5
                        }}>
                          <PersonIcon sx={{ fontSize: 12 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            {project.owner.first_name || project.owner.email}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 14 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                            {memberCount}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            участников
                          </Typography>
                        </Box>

                        {deadline && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon sx={{ fontSize: 12 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              {formatDate(deadline)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )
                }}
              </StackedCard>
            );
          })}
        </ProjectsGrid>
      ) : (
        <Box sx={{
          maxWidth: 1200,
          width: '100%',
          p: 8,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 3
        }}>
          <ScienceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {Array.isArray(projects) ? 'У вас пока нет проектов' : 'Не удалось загрузить проекты'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {Array.isArray(projects)
              ? 'Создайте свой первый научный проект для коллаборации с коллегами'
              : 'Проверьте подключение к серверу или попробуйте обновить страницу'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/projects/create')}
            size="large"
            sx={{ borderRadius: 5 }}
          >
            Создать проект
          </Button>
        </Box>
      )}

      {totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2
              }
            }}
          />
        </PaginationContainer>
      )}
    </PageContainer>
  );
}

export default ProjectsPage;